import { FC, useCallback, useMemo, useState } from 'react';

import { Cell, CellText, Checkbox, Link, SearchInput, Text, VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';
import { ChevronDownOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import FuzzySearch from 'bloko/common/fuzzySearch';

import useTrls from 'hooks/translation/useTrls';

import {
    MAX_CHECKBOX_SHOWN_COUNT,
    OVERLY_MASSIVE_FILTERS,
} from 'components/CampaignsDashboard/components/CampaignsFiltersPanel/utils';

import { CampaignFilter } from 'store/models/campaignsFilters';

interface FilterCheckboxItemProps {
    item: string;
    filterName: CampaignFilter;
    name: string;
    checked: boolean;
    onToggleFilter: (campaignFilter: CampaignFilter, id: string, checked: boolean) => void;
}

const FilterCheckboxItem: FC<FilterCheckboxItemProps> = ({ item, filterName, name, checked, onToggleFilter }) => {
    return (
        <Cell
            left={
                <Checkbox
                    data-qa={`filter-item_${filterName}_${item}`}
                    checked={checked}
                    onChange={() => onToggleFilter(filterName, item, !checked)}
                />
            }
        >
            <CellText>{name}</CellText>
        </Cell>
    );
};

const TrlKeys = {
    filters: {
        [CampaignFilter.ProductTypes]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.productTypes',
        [CampaignFilter.CampaignStatuses]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.campaignStatuses',
        [CampaignFilter.BudgetStatuses]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.budgetStatuses',
        [CampaignFilter.ModerationStatuses]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.moderationStatuses',
        [CampaignFilter.Areas]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.areas',
        [CampaignFilter.ProfessionalRoles]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.professionalRoles',
        [CampaignFilter.CreatedByUserIds]: 'CampaignsFiltersPanel.AllFiltersModal.filterName.createdByUserIds',
    },
    filterSearchPlaceholder: 'CampaignsFiltersPanel.AllFiltersModal.filterSearchPlaceholder',
    expandButton: 'CampaignsFiltersPanel.AllFiltersModal.expandButton',
};

interface AllFiltersModalFilterProps {
    filterName: CampaignFilter;
    filterList: Record<string, string>;
    checkedFilters: Set<string>;
    onToggleFilter: (filterName: CampaignFilter, id: string, checked: boolean) => void;
}

export const AllFiltersModalFilter: FC<AllFiltersModalFilterProps> = ({
    filterName,
    filterList,
    checkedFilters,
    onToggleFilter,
}) => {
    const [filterSearchValue, setFilterSearchValue] = useState('');

    const isCountGreaterThanMax = Object.values(filterList).length > MAX_CHECKBOX_SHOWN_COUNT;
    const needHideExcessCheckboxes = OVERLY_MASSIVE_FILTERS.includes(filterName);
    const [isCollapsed, setIsCollapsed] = useState(needHideExcessCheckboxes && isCountGreaterThanMax);

    const trls = useTrls();

    const searchFilterItemsByName = useCallback(
        ([, item]: [string, string]) => FuzzySearch.match(filterSearchValue, item),
        [filterSearchValue]
    );

    const filteredCheckboxes = useMemo(() => {
        const filteredFilterList = Object.entries(filterList).filter(searchFilterItemsByName);
        return isCollapsed && !filterSearchValue
            ? filteredFilterList.slice(0, MAX_CHECKBOX_SHOWN_COUNT)
            : filteredFilterList;
    }, [filterList, searchFilterItemsByName, filterSearchValue, isCollapsed]);

    const toggleExpandHandler = () => setIsCollapsed((prev) => !prev);

    return (
        <div>
            <Text typography="title-5-semibold">{trls(TrlKeys.filters[filterName])}</Text>
            {isCountGreaterThanMax && (
                <>
                    <VSpacing default={16} />
                    <SearchInput
                        data-qa={`filter-search-input_${filterName}`}
                        value={filterSearchValue}
                        onChange={setFilterSearchValue}
                        placeholder={trls(TrlKeys.filterSearchPlaceholder)}
                        clearable
                    />
                </>
            )}
            <VSpacing default={24} />
            <VSpacingContainer default={20}>
                {filteredCheckboxes.map(([item, value]) => (
                    <FilterCheckboxItem
                        key={item}
                        item={item}
                        filterName={filterName}
                        name={value}
                        checked={checkedFilters.has(item)}
                        onToggleFilter={onToggleFilter}
                    />
                ))}
                {isCollapsed && filterSearchValue === '' && (
                    <Link onClick={toggleExpandHandler} iconRight={<ChevronDownOutlinedSize16 />}>
                        {trls(TrlKeys.expandButton)}
                    </Link>
                )}
            </VSpacingContainer>
        </div>
    );
};
